import type { TransmissionThresholds } from '@beaded/models'

export type { TransmissionThresholds }

export const OFFLINE_DAYS = 14
export const WARN_DAYS = 7
export const V_OFFLINE = 5.8
export const V_WARN = 6.0

/**
 * Manufacturers whose battery is NOT on the BeadedStream 6 V scale. EWS (Orion)
 * devices run 3.0-4.1 V; the Orion import writes that voltage to
 * last_logger_transmission_ so it can be shown, and it must not be judged
 * against vOffline / vWarn or the fixed 5.9 / 6.0 colour cut-offs.
 */
const VOLTAGE_SCALE_EXEMPT_MANUFACTURERS: ReadonlySet<string> = new Set(['ews'])

export const hasBeadedStreamVoltageScale = (
  manufacturer: string | null | undefined,
): boolean =>
  manufacturer == null || !VOLTAGE_SCALE_EXEMPT_MANUFACTURERS.has(manufacturer)

/**
 * The battery to judge against voltage thresholds: the value for a
 * BeadedStream-scale logger, null (no battery signal) for an exempt one. Use it
 * for status, warnings and averages; keep showing the raw voltage.
 */
export function batteryForAlerts(
  battery: number | null | undefined,
  manufacturer: string | null | undefined,
): number | null {
  if (battery == null) return null
  return hasBeadedStreamVoltageScale(manufacturer) ? battery : null
}

/**
 * Colour tier of the fixed voltage cells (red below 5.9 V, orange below
 * 6.0 V, otherwise green); null (no colour) for an exempt manufacturer.
 */
export function voltageTier(
  value: number | null | undefined,
  manufacturer: string | null | undefined,
): 'low' | 'warn' | 'ok' | null {
  if (!hasBeadedStreamVoltageScale(manufacturer)) return null
  const v = value as number
  return v < 5.9 ? 'low' : v < 6 ? 'warn' : 'ok'
}

export const DEFAULT_THRESHOLDS: TransmissionThresholds = {
  offlineDays: OFFLINE_DAYS,
  warnDays: WARN_DAYS,
  vOffline: V_OFFLINE,
  vWarn: V_WARN,
}

export function resolveThresholds(
  t: Partial<TransmissionThresholds> | undefined,
): TransmissionThresholds {
  const safe = (n: unknown, fallback: number): number =>
    typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : fallback
  let offlineDays = safe(t?.offlineDays, OFFLINE_DAYS)
  let warnDays = safe(t?.warnDays, WARN_DAYS)
  let vOffline = safe(t?.vOffline, V_OFFLINE)
  let vWarn = safe(t?.vWarn, V_WARN)
  // Cross-field invariants: warn must trigger before offline. Days count up
  // since last transmission (warnDays < offlineDays); battery voltage counts
  // down (vWarn > vOffline). Reset the offending pair to defaults so alert
  // classification stays coherent.
  if (warnDays >= offlineDays) {
    offlineDays = OFFLINE_DAYS
    warnDays = WARN_DAYS
  }
  if (vWarn <= vOffline) {
    vOffline = V_OFFLINE
    vWarn = V_WARN
  }
  return { offlineDays, warnDays, vOffline, vWarn }
}
